import { useEffect, useState } from "react";

import { noop } from "neetocist";
import { Button, Checkbox, Modal, Typography } from "neetoui";
import PropTypes from "prop-types";
import { not } from "ramda";
import { useTranslation } from "react-i18next";

const DeleteArchiveModal = ({
  checkboxLabel = "",
  className = "",
  description = "",
  isArchiving = false,
  isDeleting = false,
  isOpen = false,
  onArchive = noop,
  onClose = noop,
  onDelete = noop,
  title = "",
}) => {
  const { t } = useTranslation();

  const [isChecked, setIsChecked] = useState(false);

  const handleClose = () => {
    setIsChecked(false);
    onClose();
  };

  useEffect(() => {
    if (not(isOpen)) setIsChecked(false);
  }, [isOpen]);

  return (
    <Modal
      {...{ className, isOpen }}
      data-testid="alert-box"
      onClose={handleClose}
    >
      <Modal.Header>
        <Typography data-testid="delete-archive-alert-title" style="h2">
          {title}
        </Typography>
      </Modal.Header>
      <Modal.Body>
        <Typography
          data-testid="delete-archive-alert-message"
          lineHeight="normal"
          style="body2"
        >
          {description}
        </Typography>
        <Checkbox
          checked={isChecked}
          className="mt-4"
          data-testid="delete-archive-alert-archive-checkbox"
          label={checkboxLabel}
          onChange={() => setIsChecked(isChecked => !isChecked)}
        />
      </Modal.Body>
      <Modal.Footer className="neeto-ui-gap-2 neeto-ui-flex neeto-ui-items-center justify-end">
        <Button
          data-testid="delete-archive-alert-archive-button"
          disabled={not(isOpen)}
          label={t("neetoMolecules.common.actions.archive")}
          loading={isArchiving}
          style="secondary"
          onClick={onArchive}
        />
        <Button
          data-testid="delete-archive-alert-delete-button"
          disabled={not(isChecked)}
          label={t("neetoMolecules.common.actions.delete")}
          loading={isDeleting}
          style="danger"
          onClick={onDelete}
        />
      </Modal.Footer>
    </Modal>
  );
};

DeleteArchiveModal.propTypes = {
  /**
   * To specify whether the modal should be opened or closed.
   */
  isOpen: PropTypes.bool,
  /**
   * To be passed to loading prop of the archive button
   */
  isArchiving: PropTypes.bool,
  /**
   * To be passed to loading prop of the delete button
   */
  isDeleting: PropTypes.bool,
  /**
   * Additional classes to be added to the modal
   */
  className: PropTypes.string,
  /**
   * Function to be called when the modal is closed
   */
  onClose: PropTypes.func,
  /**
   * Function to be called when the archive button is clicked
   */
  onArchive: PropTypes.func,
  /**
   * Function to be called when the delete button is clicked
   * */
  onDelete: PropTypes.func,
  /**
   * Title of the modal
   */
  title: PropTypes.string,
  /**
   * Description of the modal
   */
  description: PropTypes.string,
  /**
   * Label for the checkbox
   */
  checkboxLabel: PropTypes.string,
};

export default DeleteArchiveModal;
